import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  LinearProgress,
  Divider,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  Close as CloseIcon,
  DragIndicator as DragIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  ContentCopy as CopyIcon,
  Assessment as AssessmentIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  AutoAwesome as AutoAwesomeIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';

const STANDARD_BOXES = [
  { name: 'Bericap', length: 50, width: 40, height: 40, dimensions: '50x40x40cm', volume: 80000 },
  { name: 'REESES_CARAMEL', length: 50, width: 30, height: 30, dimensions: '50x30x30cm', volume: 45000 },
  { name: 'Comar', length: 61, width: 51, height: 38, dimensions: '61x51x38cm', volume: 118278 },
  { name: 'Aptar', length: 50, width: 40, height: 30, dimensions: '50x40x30cm', volume: 60000 }
];

const USA_CONSTRAINTS = {
  maxWeight: 21, // 21 kg (Amazon's weight limit)
  maxValue: 800, // USD
  minIdenticalBoxes: 5
};

const ItemCard = ({ item, onMove, onAdjustQuantity, allocatedQuantities = {} }) => {
  const [{ isDragging }, drag] = useDrag({
    type: 'item',
    item: { ...item },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  // Function to get item image URL like OutboundItem.jsx
  const getItemImageUrl = () => {
    if (!item) return '';
    
    // Try to get image from images array first (like OutboundItem.jsx line 184-195)
    if (item.images && item.images.length > 0) {
      return item.images[0].large_url;
    }
    
    // Fallback to direct image URLs
    return (
      item.image_url ||
      item.main_image_url ||
      'https://qph.cf2.quoracdn.net/main-qimg-654617264f9192ec976abe6e53356240-lq'
    );
  };

  // Calculate quantities for display
  const totalQuantity = item.originalQuantity || item.quantity || 0;
  const allocatedQuantity = allocatedQuantities[item.sku] || 0;
  const remainingQuantity = totalQuantity - allocatedQuantity;

  // Calculate quantity status for color coding based on remaining
  const getQuantityStatus = () => {
    // If remaining quantity goes negative, it means overallocated
    if (remainingQuantity < 0) return 'overallocated'; // Red
    if (remainingQuantity === 0) return 'fully_allocated'; // Green
    return 'pending_allocation'; // Yellow
  };

  const quantityStatus = getQuantityStatus();
  
  // Get background color based on quantity status
  const getBackgroundColor = () => {
    switch (quantityStatus) {
      case 'fully_allocated': return 'rgba(76, 175, 80, 0.1)'; // Light green
      case 'overallocated': return 'rgba(244, 67, 54, 0.1)'; // Light red
      case 'pending_allocation': return 'rgba(255, 193, 7, 0.1)'; // Light yellow
      default: return 'transparent';
    }
  };

  const getBorderColor = () => {
    switch (quantityStatus) {
      case 'fully_allocated': return '#4caf50'; // Green
      case 'overallocated': return '#f44336'; // Red
      case 'pending_allocation': return '#ff9800'; // Orange
      default: return '#e0e0e0';
    }
  };

  return (
    <Card
      ref={drag}
      sx={{
        mb: 0.5,
        opacity: isDragging ? 0.5 : 1,
        cursor: 'move',
        border: `2px solid ${getBorderColor()}`,
        backgroundColor: getBackgroundColor(),
        '&:hover': {
          boxShadow: 2,
          borderColor: quantityStatus === 'completed' ? '#4caf50' : 
                       quantityStatus === 'overboxed' ? '#f44336' : '#ff9800'
        }
      }}
    >
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Box display="flex" alignItems="center" gap={1.5}>
          {/* Drag Handle */}
          <DragIcon sx={{ color: 'text.secondary', fontSize: 18, flexShrink: 0 }} />
          
          {/* Product Image */}
          <Box
            component="img"
            src={getItemImageUrl()}
            alt={item.title}
            sx={{
              width: 50,
              height: 50,
              objectFit: 'cover',
              borderRadius: 1,
              flexShrink: 0,
              border: '1px solid #e0e0e0'
            }}
            onError={(e) => {
              e.target.src = 'https://qph.cf2.quoracdn.net/main-qimg-654617264f9192ec976abe6e53356240-lq';
            }}
          />
          
          {/* Product Info */}
          <Box flex={1} minWidth={0}>
            <Typography variant="body2" fontWeight="bold" sx={{ 
              lineHeight: 1.3, 
              mb: 0.5,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              color: 'primary.main'
            }}>
              {item.title || item.sku}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ 
              fontSize: '0.75rem',
              lineHeight: 1.2,
              display: 'block',
              fontWeight: 'medium'
            }}>
              {item.sku}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ 
              fontSize: '0.7rem',
              lineHeight: 1.2,
              display: 'block'
            }}>
              {item.asin}
            </Typography>
            
            {/* Weight and Value with better visibility */}
            <Box display="flex" gap={1.5} mt={0.75}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 0.5,
                px: 1,
                py: 0.25,
                borderRadius: 1,
                backgroundColor: 'primary.main',
                color: 'white'
              }}>
                <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>
                  {item.weight.toFixed(2)}kg
                </Typography>
                {item.isEstimatedWeight && item.hasEstimatedData && (
                  <Tooltip title="Estimated weight from ASIN database">
                    <InfoIcon sx={{ fontSize: '0.75rem', color: 'white' }} />
                  </Tooltip>
                )}
              </Box>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 0.5,
                px: 1,
                py: 0.25,
                borderRadius: 1,
                backgroundColor: 'secondary.main',
                color: 'white'
              }}>
                <Typography variant="caption" sx={{ fontSize: '0.75rem', fontWeight: 'bold' }}>
                  ${item.value.toFixed(2)}
                </Typography>
              </Box>
            </Box>
            
            {/* Dimensions if available */}
            {item.length && item.width && item.height && (
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 0.5,
                mt: 0.5
              }}>
                <Typography variant="caption" sx={{ 
                  fontSize: '0.65rem', 
                  color: 'text.secondary',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5
                }}>
                  {item.length.toFixed(1)} × {item.width.toFixed(1)} × {item.height.toFixed(1)} cm
                  {item.hasEstimatedData && (
                    <Tooltip title="Estimated dimensions from ASIN database">
                      <InfoIcon sx={{ fontSize: '0.65rem', color: 'text.secondary' }} />
                    </Tooltip>
                  )}
                </Typography>
              </Box>
            )}
          </Box>
          
          {/* Quantity Controls */}
          <Box display="flex" alignItems="center" flexShrink={0} sx={{ 
            flexDirection: 'column',
            gap: 0.5
          }}>
            {/* Quantity Status Badge */}
            <Box sx={{
              px: 1,
              py: 0.25,
              borderRadius: 1,
              fontSize: '0.7rem',
              fontWeight: 'bold',
              color: 'white',
              backgroundColor: quantityStatus === 'fully_allocated' ? '#4caf50' : 
                             quantityStatus === 'overallocated' ? '#f44336' : '#ff9800',
              textAlign: 'center',
              minWidth: 70
            }}>
              {quantityStatus === 'fully_allocated' ? 'Allocated' : 
               quantityStatus === 'overallocated' ? 'Over' : 
               'Pending'}
            </Box>
            
            {/* Quantity Display and Controls */}
            <Box display="flex" alignItems="center">
              <Box textAlign="center" sx={{ mx: 0.75, minWidth: 60 }}>
                <Typography variant="h6" sx={{ 
                  fontWeight: 'bold', 
                  display: 'block',
                  fontSize: '1.1rem',
                  color: quantityStatus === 'fully_allocated' ? '#4caf50' : 
                         quantityStatus === 'overallocated' ? '#f44336' : '#ff9800'
                }}>
                  {remainingQuantity}
                </Typography>
                <Typography variant="caption" sx={{ 
                  fontSize: '0.65rem', 
                  color: 'text.secondary',
                  display: 'block',
                  fontWeight: 'medium'
                }}>
                  / {totalQuantity} total
                </Typography>
                {remainingQuantity > 0 && (
                  <Typography variant="caption" sx={{ 
                    fontSize: '0.55rem', 
                    color: 'primary.main',
                    display: 'block',
                    fontStyle: 'italic'
                  }}>
                    Drag to distribute
                  </Typography>
                )}
              </Box>
              <IconButton
                size="small"
                onClick={() => onAdjustQuantity(item.id, 1)}
                sx={{ p: 0.25, color: 'text.secondary' }}
              >
                <AddIcon sx={{ fontSize: '0.9rem' }} />
              </IconButton>
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

const BoxGroup = ({ groupName, groupData, onDrop, onRemoveBox, onDuplicateBox, onAdjustBoxItemQuantity }) => {
  const [{ isOver }, drop] = useDrop({
    accept: 'item',
    drop: (item) => onDrop(item, groupName),
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  // Function to get item image URL similar to OutboundItem.jsx
  const getItemImageUrl = (item) => {
    if (!item) return '';
    
    // Try to get image from images array first (like OutboundItem.jsx line 184-195)
    if (item.images && item.images.length > 0) {
      return item.images[0].large_url;
    }
    
    // Fallback to direct image URLs
    return (
      item.image_url ||
      item.main_image_url ||
      'https://qph.cf2.quoracdn.net/main-qimg-654617264f9192ec976abe6e53356240-lq'
    );
  };

  if (!groupData) return null;
  
  const boxes = groupData.boxes || [];
  const totalWeight = boxes.reduce((sum, box) => sum + box.weight, 0); // Weight in kg (native)
  const totalValue = boxes.reduce((sum, box) => sum + box.value, 0);
  const hasMinimumBoxes = groupData.qualifiesForZeroFees;
  
  // Calculate placement fee status
  const placementFeeStatus = hasMinimumBoxes ? '$0.00' : 'Standard fees apply';
  const avgWeightPerBox = boxes.length > 0 ? totalWeight / boxes.length : 0;
  const avgValuePerBox = boxes.length > 0 ? totalValue / boxes.length : 0;

  return (
    <Paper
      ref={drop}
      sx={{
        p: 1.5,
        minHeight: 180,
        border: isOver ? '2px dashed #1976d2' : '1px solid #e0e0e0',
        backgroundColor: isOver ? '#f5f5f5' : 'white',
        borderRadius: 2,
        position: 'relative'
      }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
        <Box>
          <Typography variant="subtitle2" color="primary" fontWeight="bold">
            Box Group {groupData.groupIndex}
          </Typography>
          {groupData.selectedBoxType && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.65rem' }}>
              {groupData.selectedBoxType} ({groupData.boxDimensions})
              {groupData.volumeUtilization && (
                <span style={{ 
                  color: groupData.volumeUtilization >= 70 ? '#4caf50' : groupData.volumeUtilization >= 50 ? '#ff9800' : '#f44336',
                  fontWeight: 'bold'
                }}>
                  {` • ${groupData.volumeUtilization.toFixed(1)}% utilization`}
                </span>
              )}
              {groupData.packingArrangement && (
                <span style={{ fontStyle: 'italic', fontSize: '0.6rem' }}>
                  {` • ${groupData.packingArrangement}`}
                </span>
              )}
            </Typography>
          )}
        </Box>
        <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
          <Chip
            size="small"
            label={`${boxes.length} boxes`}
            color={hasMinimumBoxes ? 'success' : 'warning'}
            icon={hasMinimumBoxes ? <CheckCircleIcon /> : <WarningIcon />}
            sx={{ fontSize: '0.7rem', height: 24 }}
          />
          {groupData.volumeUtilization && (
            <Chip
              size="small"
              label={`${groupData.volumeUtilization.toFixed(1)}% full`}
              color={groupData.volumeUtilization >= 70 ? 'success' : groupData.volumeUtilization >= 50 ? 'warning' : 'error'}
              variant="filled"
              sx={{ fontSize: '0.65rem', height: 20, fontWeight: 'bold' }}
            />
          )}
          {hasMinimumBoxes && (
            <Chip
              size="small"
              label="Zero Fees"
              color="success"
              variant="outlined"
              sx={{ fontSize: '0.65rem', height: 20 }}
            />
          )}
        </Box>
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        {hasMinimumBoxes ? 
          `${boxes.length} identical boxes qualify for zero placement fees` :
          `${boxes.length} boxes - need ${USA_CONSTRAINTS.minIdenticalBoxes - boxes.length} more for zero fees`
        }
        {groupData.packingReasoning && (
          <Box component="span" sx={{ display: 'block', fontStyle: 'italic', fontSize: '0.6rem', mt: 0.5 }}>
            📦 {groupData.packingReasoning}
          </Box>
        )}
        {groupData.costJustification && (
          <Box component="span" sx={{ display: 'block', fontStyle: 'italic', fontSize: '0.6rem', mt: 0.5, color: 'success.main' }}>
            💰 {groupData.costJustification}
          </Box>
        )}
      </Typography>
      
      {/* Real-time totals display */}
      <Box display="flex" gap={2} mb={1}>
        <Typography variant="caption" color="text.secondary">
          <strong>Weight per Box:</strong> {avgWeightPerBox.toFixed(1)} kg
        </Typography>
        <Typography variant="caption" color="text.secondary">
          <strong>Value per Box:</strong> ${avgValuePerBox.toFixed(0)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          <strong>Placement Fees:</strong> {placementFeeStatus}
        </Typography>
      </Box>

      {boxes.length > 0 && (
        <Card
          sx={{
            border: '1px solid #e0e0e0',
            backgroundColor: '#fafafa'
          }}
        >
          <CardContent sx={{ p: 2 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
              <Typography variant="subtitle2" fontWeight="bold">
                {boxes.length === 1 ? 
                  `Box 1` : 
                  `Boxes 1-${boxes.length}`} 
                <Typography component="span" color="text.secondary" sx={{ ml: 1, fontSize: '0.9em' }}>
                  ({boxes.length} identical box{boxes.length > 1 ? 'es' : ''})
                </Typography>
              </Typography>
              <Box display="flex" gap={0.5} flexWrap="wrap">
                <Chip
                  size="small"
                  label={`${boxes[0].weight.toFixed(1)} kg`}
                  color={boxes[0].hasWeightViolation ? 'error' : 'primary'}
                  variant="filled"
                  sx={{ 
                    fontSize: '0.75rem', 
                    height: 24,
                    fontWeight: 'bold',
                    color: 'white'
                  }}
                />
                <Chip
                  size="small"
                  label={`$${boxes[0].value.toFixed(0)}`}
                  color={boxes[0].hasValueViolation ? 'error' : 'secondary'}
                  variant="filled"
                  sx={{ 
                    fontSize: '0.75rem', 
                    height: 24,
                    fontWeight: 'bold',
                    color: 'white'
                  }}
                />
              </Box>
            </Box>
            
            {(boxes[0].hasValueViolation || boxes[0].hasWeightViolation) && (
              <Alert severity="error" sx={{ mb: 1 }}>
                <Typography variant="caption">
                  {boxes[0].hasValueViolation && `Value exceeds $${USA_CONSTRAINTS.maxValue} limit`}
                  {boxes[0].hasValueViolation && boxes[0].hasWeightViolation && ' and '}
                  {boxes[0].hasWeightViolation && `Weight exceeds ${USA_CONSTRAINTS.maxWeight} kg limit`}
                  {' - this configuration may be rejected'}
                </Typography>
              </Alert>
            )}
            
            {/* Items list - one line per item like OutboundItem */}
            <Box sx={{ maxHeight: '300px', overflowY: 'auto' }}>
              {console.log('DEBUG: Rendering items for group:', groupName, 'boxes:', boxes, 'first box items:', boxes[0]?.items)}
              {boxes[0]?.items?.length > 0 ? boxes[0].items.map((item, itemIndex) => (
                <Box 
                  key={itemIndex}
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1.5, 
                    py: 1, 
                    px: 0.5,
                    borderBottom: itemIndex < boxes[0].items.length - 1 ? '1px solid #e0e0e0' : 'none',
                    '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.02)' }
                  }}
                >
                  {/* Product Image */}
                  <Box
                    component="img"
                    src={getItemImageUrl(item)}
                    alt={item.title}
                    sx={{
                      width: 40,
                      height: 40,
                      objectFit: 'cover',
                      borderRadius: 1,
                      flexShrink: 0,
                      border: '1px solid #e0e0e0'
                    }}
                    onError={(e) => {
                      e.target.src = 'https://qph.cf2.quoracdn.net/main-qimg-654617264f9192ec976abe6e53356240-lq';
                    }}
                  />
                  
                  {/* Product Info */}
                  <Box flex={1} minWidth={0}>
                    <Typography 
                      variant="caption" 
                      fontWeight="bold"
                      sx={{ 
                        display: 'block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontSize: '0.75rem',
                        color: 'primary.main'
                      }}
                    >
                      {item.sku}
                    </Typography>
                    <Typography 
                      variant="caption" 
                      color="text.secondary"
                      sx={{ 
                        display: 'block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        fontSize: '0.65rem',
                        lineHeight: 1.2
                      }}
                    >
                      {item.title}
                    </Typography>
                    <Box display="flex" gap={1} mt={0.25} flexWrap="wrap">
                      <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>
                        {item.weight.toFixed(2)}kg
                      </Typography>
                      <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>
                        ${item.value?.toFixed(2)}
                      </Typography>
                      {item.length && item.width && item.height && (
                        <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.secondary' }}>
                          {item.length.toFixed(1)}×{item.width.toFixed(1)}×{item.height.toFixed(1)}cm
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  
                  {/* Quantity Controls */}
                  <Box display="flex" alignItems="center" flexShrink={0}>
                    <IconButton
                      size="small"
                      onClick={() => onAdjustBoxItemQuantity && onAdjustBoxItemQuantity(groupName, item.sku, -1)}
                      sx={{ p: 0.25, color: 'text.secondary' }}
                    >
                      <RemoveIcon sx={{ fontSize: '0.8rem' }} />
                    </IconButton>
                    <Box sx={{ mx: 0.75, minWidth: 20, textAlign: 'center' }}>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          display: 'block',
                          fontWeight: 'bold', 
                          fontSize: '0.75rem',
                          color: 'primary.main'
                        }}
                      >
                        {item.quantity}
                      </Typography>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          display: 'block',
                          fontSize: '0.6rem',
                          color: 'text.secondary',
                          lineHeight: 1
                        }}
                      >
                        ({item.quantity * boxes.length})
                      </Typography>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={() => onAdjustBoxItemQuantity && onAdjustBoxItemQuantity(groupName, item.sku, 1)}
                      sx={{ p: 0.25, color: 'text.secondary' }}
                    >
                      <AddIcon sx={{ fontSize: '0.8rem' }} />
                    </IconButton>
                  </Box>
                </Box>
              )) : (
                <Box sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
                  <Typography variant="caption">
                    No items in this box group
                  </Typography>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>
      )}

      {boxes.length === 0 && (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          minHeight={80}
          color="text.secondary"
        >
          <Typography variant="caption" textAlign="center">
            Drop items here to create boxes
          </Typography>
        </Box>
      )}
      
      {isOver && boxes.length > 0 && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(25, 118, 210, 0.1)',
            border: '2px dashed #1976d2',
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10
          }}
        >
          <Typography variant="body2" color="primary" fontWeight="bold">
            Drop to distribute across {boxes.length} boxes
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

const PlanPlacementFeesModal = ({ open, onClose, shipmentData, asinData }) => {
  const [items, setItems] = useState([]);
  const [boxGroups, setBoxGroups] = useState({});
  const [optimizing, setOptimizing] = useState(false);
  const [autoOptimize, setAutoOptimize] = useState(true);
  const [optimizationReasoning, setOptimizationReasoning] = useState('');
  const [standardBoxes, setStandardBoxes] = useState([]);

  // Fetch standard boxes from database
  useEffect(() => {
    const fetchStandardBoxes = async () => {
      try {
        const response = await fetch('/api/get_standard_boxes');
        if (response.ok) {
          const boxes = await response.json();
          setStandardBoxes(boxes);
          console.log('Fetched standard boxes:', boxes);
        }
      } catch (error) {
        console.error('Error fetching standard boxes:', error);
      }
    };
    
    if (open) {
      fetchStandardBoxes();
    }
  }, [open]);

  // Calculate allocated quantities for display purposes
  const getAllocatedQuantities = () => {
    const allocatedQuantities = {};
    
    console.log('DEBUG: boxGroups structure:', boxGroups);
    
    Object.values(boxGroups).forEach(group => {
      console.log('DEBUG: group structure:', group);
      if (group.boxes) {
        group.boxes.forEach(box => {
          box.items.forEach(item => {
            allocatedQuantities[item.sku] = (allocatedQuantities[item.sku] || 0) + item.quantity;
          });
        });
      }
    });
    
    console.log('DEBUG: allocatedQuantities:', allocatedQuantities);
    return allocatedQuantities;
  };

  useEffect(() => {
    if (shipmentData && shipmentData.items && asinData) {
      console.log('=== DEBUGGING ASIN DATA ===');
      console.log('asinData array:', asinData);
      console.log('asinData length:', asinData.length);
      console.log('Available ASINs in asinData:', asinData.map(item => item.ASIN));
      if (asinData.length > 0) {
        console.log('Sample asinData item fields:', Object.keys(asinData[0]));
        console.log('First asinData item:', asinData[0]);
      }
      
      // Convert shipment items to planning format (using same logic as OutboundItem.jsx)
      const planningItems = shipmentData.items.map(shipmentItem => {
        console.log(`Looking for ASIN: ${shipmentItem.item.asin}`);
        
        // Find matching ASIN data using exact same logic as OutboundItem.jsx
        const getAsinData = (asin) => {
          return asinData.find(data => data.ASIN === asin) || {};
        };
        
        const asinDataItem = getAsinData(shipmentItem.item.asin);
        console.log(`Found asinDataItem for ${shipmentItem.item.asin}:`, asinDataItem);
        
        // Get weight in kg (no conversion needed)
        let weight = 0;
        let isEstimatedWeight = false;
        
        if (asinDataItem.WEIGHT && !isNaN(parseFloat(asinDataItem.WEIGHT))) {
          weight = parseFloat(asinDataItem.WEIGHT); // Keep in kg
          isEstimatedWeight = false;
        } else {
          // No weight found in asinData, will fetch from ASIN_INFO later
          weight = 0;
          isEstimatedWeight = true;
        }
        
        // Get value exactly like OutboundItem.jsx (line 583)  
        let value = parseFloat(asinDataItem.VALUE || 0);

        // Try to get dimensions from asinData first
        let length = asinDataItem.LENGTH ? parseFloat(asinDataItem.LENGTH) : null;
        let width = asinDataItem.WIDTH ? parseFloat(asinDataItem.WIDTH) : null;
        let height = asinDataItem.HEIGHT ? parseFloat(asinDataItem.HEIGHT) : null;
        let hasEstimatedData = false;

        const item = {
          id: shipmentItem.id,
          sku: shipmentItem.item.merchant_sku,
          asin: shipmentItem.item.asin,
          fnsku: shipmentItem.item.fnsku,
          weight: weight, // Weight in kg (native)
          value: value,
          quantity: shipmentItem.quantity || 1,
          originalQuantity: shipmentItem.quantity || 1,
          title: shipmentItem.item.title,
          images: shipmentItem.item.images, // Include images for display
          isEstimatedWeight: isEstimatedWeight,
          hasEstimatedData: hasEstimatedData,
          // Store original data for reference
          asinWeight: asinDataItem.WEIGHT,
          asinValue: asinDataItem.VALUE,
          // Dimensions (will be updated if fetched from ASIN_INFO)
          length: length,
          width: width,
          height: height
        };
        
        console.log(`${item.sku}: ${weight.toFixed(2)}kg, $${value.toFixed(2)}, qty=${item.quantity}, dims=${length || 'N/A'}×${width || 'N/A'}×${height || 'N/A'}cm (from asinData: ${asinDataItem.WEIGHT}kg, $${asinDataItem.VALUE})`);
        
        return item;
      });
      
      console.log('Planning items with weight/value:', planningItems);
      setItems(planningItems);
    }
  }, [shipmentData, asinData]);

  // Fetch estimated weights and dimensions from ASIN_INFO for all items
  useEffect(() => {
    const fetchEstimatedData = async () => {
      if (!items.length) return;
      
      // Fetch for all items to get dimensions, not just those missing weight
      const itemsNeedingData = items.filter(item => 
        (item.isEstimatedWeight && item.weight === 0) || // Missing weight
        (!item.length || !item.width || !item.height) // Missing dimensions
      );
      
      console.log(`Items needing data check:`, items.map(item => ({
        sku: item.sku,
        hasWeight: item.weight > 0,
        hasDimensions: !!(item.length && item.width && item.height),
        length: item.length,
        width: item.width,
        height: item.height
      })));
      
      if (itemsNeedingData.length === 0) {
        console.log('No items need data fetching');
        return;
      }

      console.log(`Fetching ASIN_INFO data for ${itemsNeedingData.length} items:`, itemsNeedingData.map(i => i.sku));

      const updatePromises = itemsNeedingData.map(async (item) => {
        try {
          const response = await fetch(`/api/asin-info/${item.asin}`);
          if (response.ok) {
            const asinInfo = await response.json();
            if (asinInfo) {
              console.log(`Found ASIN info for ${item.asin}:`, asinInfo);
              return {
                ...item,
                // Use existing weight if available, otherwise use from asinInfo
                weight: item.weight > 0 ? item.weight : (asinInfo.WeightKg || item.weight),
                length: asinInfo.Length ? (parseFloat(asinInfo.Length) / 10) : item.length, // Convert mm to cm
                width: asinInfo.Width ? (parseFloat(asinInfo.Width) / 10) : item.width,
                height: asinInfo.Height ? (parseFloat(asinInfo.Height) / 10) : item.height,
                isEstimatedWeight: item.isEstimatedWeight || (asinInfo.WeightKg && item.weight === 0),
                hasEstimatedData: true
              };
            }
          }
        } catch (error) {
          console.error(`Error fetching ASIN info for ${item.asin}:`, error);
        }
        return item;
      });

      const updatedItems = await Promise.all(updatePromises);
      
      // Update items with estimated data
      setItems(prevItems => 
        prevItems.map(item => {
          const updatedItem = updatedItems.find(updated => updated.id === item.id);
          return updatedItem || item;
        })
      );
    };

    fetchEstimatedData();
  }, [items.length]); // Only run when items are first loaded

  const handleDrop = (item, groupName) => {
    const group = boxGroups[groupName];
    
    if (!group || !group.boxes || group.boxes.length === 0) {
      // No existing boxes in group - create a new box with 1 item
      const newBox = {
        items: [{ ...item, quantity: 1 }],
        weight: item.weight,
        value: item.value
      };
      
      setBoxGroups(prev => ({
        ...prev,
        [groupName]: {
          ...group,
          boxes: [newBox],
          qualifiesForZeroFees: false,
          boxCount: 1,
          groupIndex: group?.groupIndex || Object.keys(prev).length + 1
        }
      }));
      return;
    }
    
    // Get remaining quantity for this item
    const allocatedQuantities = getAllocatedQuantities();
    const remainingQuantity = item.quantity - (allocatedQuantities[item.sku] || 0);
    
    if (remainingQuantity <= 0) {
      alert(`No remaining quantity for ${item.sku}`);
      return;
    }
    
    const boxCount = group.boxes.length;
    const itemsPerBox = Math.floor(remainingQuantity / boxCount);
    const remainder = remainingQuantity % boxCount;
    
    if (itemsPerBox === 0) {
      alert(`Cannot distribute ${remainingQuantity} items across ${boxCount} boxes. Need at least ${boxCount} items to add 1 per box.`);
      return;
    }
    
    // Check if this would break the identical box pattern
    if (remainder > 0) {
      const confirmMessage = `You have ${remainingQuantity} items for ${boxCount} boxes. This will add ${itemsPerBox} per box with ${remainder} leftover.\n\nThis will break the identical box pattern. Continue?`;
      if (!confirm(confirmMessage)) {
        return;
      }
    }
    
    // Update all boxes in the group to add the item
    const updatedBoxes = group.boxes.map((box, boxIndex) => {
      const existingItem = box.items.find(boxItem => boxItem.sku === item.sku);
      let updatedItems;
      
      if (existingItem) {
        // Item already exists in box - increase quantity
        updatedItems = box.items.map(boxItem => 
          boxItem.sku === item.sku 
            ? { ...boxItem, quantity: boxItem.quantity + itemsPerBox }
            : boxItem
        );
      } else {
        // Add new item to box
        updatedItems = [...box.items, { 
          ...item, 
          quantity: itemsPerBox,
          id: item.id,
          sku: item.sku,
          asin: item.asin,
          fnsku: item.fnsku,
          title: item.title,
          images: item.images
        }];
      }
      
      // Recalculate box weight and value
      const totalWeight = updatedItems.reduce((sum, boxItem) => {
        const originalItem = items.find(i => i.sku === boxItem.sku);
        return sum + (originalItem ? originalItem.weight * boxItem.quantity : 0);
      }, 0);
      
      const totalValue = updatedItems.reduce((sum, boxItem) => {
        const originalItem = items.find(i => i.sku === boxItem.sku);
        return sum + (originalItem ? originalItem.value * boxItem.quantity : 0);
      }, 0);
      
      return {
        ...box,
        items: updatedItems,
        weight: totalWeight,
        value: totalValue,
        hasWeightViolation: totalWeight > USA_CONSTRAINTS.maxWeight,
        hasValueViolation: totalValue > USA_CONSTRAINTS.maxValue
      };
    });
    
    // Update the group
    const updatedGroup = {
      ...group,
      boxes: updatedBoxes,
      qualifiesForZeroFees: boxCount >= USA_CONSTRAINTS.minIdenticalBoxes && remainder === 0 // Only qualifies if no remainder
    };
    
    setBoxGroups(prev => ({
      ...prev,
      [groupName]: updatedGroup
    }));
    
    // Show success message
    const totalAdded = itemsPerBox * boxCount;
    alert(`Added ${totalAdded} units of ${item.sku} (${itemsPerBox} per box across ${boxCount} boxes)${remainder > 0 ? `. ${remainder} units remaining.` : ''}`);
  };

  const handleRemoveBox = (boxType, index) => {
    const box = boxGroups[boxType][index];
    
    // Return items to available pool
    box.items.forEach(boxItem => {
      setItems(prev => {
        const existingItem = prev.find(i => i.id === boxItem.id);
        if (existingItem) {
          return prev.map(i =>
            i.id === boxItem.id
              ? { ...i, quantity: i.quantity + boxItem.quantity }
              : i
          );
        } else {
          return [...prev, { ...boxItem }];
        }
      });
    });

    // Remove box
    setBoxGroups(prev => ({
      ...prev,
      [boxType]: prev[boxType].filter((_, i) => i !== index)
    }));
  };

  const handleDuplicateBox = (boxType) => {
    const boxes = boxGroups[boxType];
    if (boxes.length > 0) {
      const lastBox = boxes[boxes.length - 1];
      const newBox = {
        items: [...lastBox.items],
        weight: lastBox.weight,
        value: lastBox.value
      };
      
      setBoxGroups(prev => ({
        ...prev,
        [boxType]: [...prev[boxType], newBox]
      }));
    }
  };

  const handleAdjustQuantity = (itemId, delta) => {
    setItems(prev =>
      prev.map(item =>
        item.id === itemId
          ? { ...item, quantity: Math.max(0, item.quantity + delta) }
          : item
      ).filter(item => item.quantity > 0)
    );
  };

  const handleAdjustBoxItemQuantity = (groupName, sku, delta) => {
    setBoxGroups(prev => {
      const group = prev[groupName];
      if (!group || !group.boxes || group.boxes.length === 0) return prev;

      const updatedBoxes = group.boxes.map(box => {
        const updatedItems = box.items.map(item => {
          if (item.sku === sku) {
            const newQuantity = Math.max(0, item.quantity + delta);
            // If quantity becomes 0, this item will be filtered out
            if (newQuantity === 0) {
              return null; // Mark for removal
            }
            // Update individual item weight and value
            const originalItem = items.find(i => i.sku === sku) || 
                               shipmentData.items.find(si => si.item.merchant_sku === sku);
            if (originalItem) {
              return {
                ...item,
                quantity: newQuantity,
                weight: originalItem.weight * newQuantity, // Weight in kg (native)
                value: originalItem.value * newQuantity
              };
            }
          }
          return item;
        }).filter(item => item !== null); // Remove items with quantity 0

        // Recalculate total box weight and value
        const totalWeight = updatedItems.reduce((sum, item) => sum + item.weight, 0); // kg (native)
        const totalValue = updatedItems.reduce((sum, item) => sum + item.value, 0);

        return {
          ...box,
          items: updatedItems,
          weight: totalWeight, // Weight in kg (native)
          value: totalValue,
          hasWeightViolation: totalWeight > USA_CONSTRAINTS.maxWeight,
          hasValueViolation: totalValue > USA_CONSTRAINTS.maxValue
        };
      });

      // Recalculate group-level metrics
      const updatedGroup = {
        ...group,
        boxes: updatedBoxes
      };

      // Update qualification for zero fees (minimum identical boxes)
      updatedGroup.qualifiesForZeroFees = updatedBoxes.length >= USA_CONSTRAINTS.minIdenticalBoxes;
      updatedGroup.boxCount = updatedBoxes.length;

      return {
        ...prev,
        [groupName]: updatedGroup
      };
    });
  };

  const handleAutoOptimize = async () => {
    setOptimizing(true);
    
    try {
      // Prepare standard boxes data for AI
      const availableStandardBoxes = [
        ...STANDARD_BOXES,
        ...standardBoxes.map(box => ({
          name: box.NAME,
          length: parseFloat(box.LENGTH),
          width: parseFloat(box.WIDTH), 
          height: parseFloat(box.HEIGHT),
          volume: parseFloat(box.LENGTH) * parseFloat(box.WIDTH) * parseFloat(box.HEIGHT)
        }))
      ];
      
      // Dynamic token calculation for o3-mini (includes reasoning tokens)
      const baseTokens = 24000; // Base response size + reasoning overhead (3x larger)
      const tokensPerItem = 600; // Estimated tokens per SKU (reasoning + output) (3x larger)
      const dynamicTokenLimit = Math.max(24000, baseTokens + (items.length * tokensPerItem));
      const maxTokenLimit = 90000; // Higher cap for complex shipments (3x larger)
      const finalTokenLimit = Math.min(dynamicTokenLimit, maxTokenLimit);
      
      console.log(`Token calculation: ${items.length} items, estimated ${finalTokenLimit} tokens needed`);
      
      const response = await fetch('/api/optimize-with-claude', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          items: items,
          standardBoxes: standardBoxes
        })
      });

      const data = await response.json();
      
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Optimization failed');
      }

      const { strategy, result, usage } = data;
      
      console.log('Response usage:', usage);
      console.log('Claude Strategy:', strategy);
      console.log('Claude Result:', result);
      
      // Apply the optimization result
      const optimizedBoxes = result.primary_recommendation?.optimized_boxes || result.optimized_boxes || [];
      if (optimizedBoxes.length > 0) {
        console.log('Claude Response:', result);
        
        // Clear current boxes
        setBoxGroups({});
        
        // Process optimized boxes using the new structured format
        let totalBoxesCreated = 0;
        let qualifyingGroups = 0;
        const allocatedItems = {};
        const newBoxGroups = {};
        
        // Process each box group from Claude's response
        optimizedBoxes.forEach((boxGroup, groupIndex) => {
          const boxCount = boxGroup.box_count || 1;
          const selectedBoxType = boxGroup.box_type || 'Custom';
          const itemsPerBox = boxGroup.items_per_box || [];
          
          // Skip groups with fewer than 5 boxes - these shouldn't exist but check anyway
          if (boxCount < USA_CONSTRAINTS.minIdenticalBoxes) {
            console.warn(`Rejecting box group with only ${boxCount} boxes - minimum is ${USA_CONSTRAINTS.minIdenticalBoxes}`);
            // Move items to leftovers instead of creating inefficient groups
            itemsPerBox.forEach(item => {
              const totalRejected = item.quantity * boxCount;
              console.log(`Moving ${totalRejected} units of ${item.sku} to leftovers (from rejected ${boxCount}-box group)`);
            });
            return; // Skip this group
          }
          
          totalBoxesCreated += boxCount;
          qualifyingGroups++; // All groups that pass the filter qualify for zero fees
          
          // Track allocation for each SKU in this group
          itemsPerBox.forEach(item => {
            const totalAllocated = item.quantity * boxCount;
            allocatedItems[item.sku] = (allocatedItems[item.sku] || 0) + totalAllocated;
            console.log(`Allocating ${totalAllocated} units of ${item.sku} (${item.quantity} per box × ${boxCount} boxes)`);
          });
          
          // Create identical boxes for this group
          const boxesToCreate = [];
          for (let i = 0; i < boxCount; i++) {
            let boxItems = [];
            
            // Add all SKUs for this box type
            itemsPerBox.forEach(item => {
              const originalItem = items.find(origItem => origItem.sku === item.sku);
              if (originalItem) {
                boxItems.push({
                  id: originalItem.id,
                  sku: item.sku,
                  title: originalItem.title,
                  asin: originalItem.asin,
                  fnsku: originalItem.fnsku,
                  quantity: item.quantity,
                  weight: (item.weight_per_unit || originalItem.weight) * item.quantity,
                  value: (item.value_per_unit || originalItem.value) * item.quantity,
                  images: originalItem.images
                });
              }
            });
            
            // Calculate box weight and value from items
            const boxWeight = boxGroup.box_weight || boxItems.reduce((sum, item) => sum + item.weight, 0);
            const boxValue = boxGroup.box_value || boxItems.reduce((sum, item) => sum + item.value, 0);
            
            // Validate constraints
            if (boxWeight > USA_CONSTRAINTS.maxWeight) {
              console.warn(`Box weight ${boxWeight.toFixed(1)} kg exceeds limit of ${USA_CONSTRAINTS.maxWeight} kg`);
            }
            if (boxValue > USA_CONSTRAINTS.maxValue) {
              console.warn(`Box value $${boxValue.toFixed(0)} exceeds limit of $${USA_CONSTRAINTS.maxValue}`);
            }
            
            console.log(`Creating box ${i + 1} for group ${groupIndex + 1}:`, boxItems);
            
            boxesToCreate.push({
              items: boxItems,
              weight: boxWeight,
              weightKg: boxWeight,
              value: boxValue,
              hasWeightViolation: boxWeight > USA_CONSTRAINTS.maxWeight,
              hasValueViolation: boxValue > USA_CONSTRAINTS.maxValue
            });
          }
          
          // Create a dynamic group name based on the group index and qualification status
          const groupName = `group${groupIndex + 1}`;
          newBoxGroups[groupName] = {
            boxes: boxesToCreate,
            qualifiesForZeroFees: boxGroup.qualifies_for_zero_fees || (boxCount >= USA_CONSTRAINTS.minIdenticalBoxes),
            boxCount: boxCount,
            groupIndex: groupIndex + 1,
            selectedBoxType: selectedBoxType,
            boxDimensions: boxGroup.box_dimensions || 'Estimated from Claude',
            volumeUtilization: boxGroup.volume_utilization || 0,
            boxVolume: boxGroup.box_volume || 0,
            usedVolume: boxGroup.used_volume || 0,
            packingArrangement: boxGroup.packing_arrangement || '',
            packingReasoning: boxGroup.packing_reasoning || `${itemsPerBox.length} SKUs packed together in ${selectedBoxType} boxes`,
            costJustification: (boxGroup.qualifies_for_zero_fees || boxCount >= USA_CONSTRAINTS.minIdenticalBoxes) ? 'Qualifies for zero placement fees' : 'Standard placement fees apply'
          };
        });
        
        console.log('Setting box groups:', newBoxGroups);
        
        // Validate total allocations don't exceed inventory
        const totalAllocatedByItem = {};
        Object.values(newBoxGroups).forEach(group => {
          group.boxes.forEach(box => {
            box.items.forEach(item => {
              totalAllocatedByItem[item.sku] = (totalAllocatedByItem[item.sku] || 0) + item.quantity;
            });
          });
        });
        
        // Check for overallocations
        const overallocations = [];
        items.forEach(item => {
          const allocated = totalAllocatedByItem[item.sku] || 0;
          if (allocated > item.originalQuantity) {
            overallocations.push({
              sku: item.sku,
              available: item.originalQuantity,
              allocated: allocated,
              excess: allocated - item.originalQuantity
            });
          }
        });
        
        if (overallocations.length > 0) {
          const errorMsg = `CRITICAL ERROR: Claude allocated more units than available!\n\n` +
            overallocations.map(item => 
              `${item.sku}: Allocated ${item.allocated} but only ${item.available} available (excess: ${item.excess})`
            ).join('\n') + 
            `\n\nThis is a math error in the optimization. Please try again.`;
          throw new Error(errorMsg);
        }
        
        setBoxGroups(newBoxGroups);
        
        // Items will automatically show correct allocated/remaining counts via getAllocatedQuantities()
        console.log('Allocated items summary:', allocatedItems);
        
        // Check for constraint violations
        const violatingGroups = Object.values(newBoxGroups).filter(group => 
          group.boxes.some(box => box.hasWeightViolation || box.hasValueViolation)
        );
        
        // If there are violations, reject the response and show error
        if (violatingGroups.length > 0) {
          const violationDetails = violatingGroups.map(group => {
            const violations = group.boxes.filter(box => box.hasWeightViolation || box.hasValueViolation);
            return violations.map(box => {
              let details = [];
              if (box.hasWeightViolation) details.push(`Weight: ${box.weight.toFixed(1)} kg (limit: ${USA_CONSTRAINTS.maxWeight} kg)`);
              if (box.hasValueViolation) details.push(`Value: $${box.value.toFixed(0)} (limit: $${USA_CONSTRAINTS.maxValue})`);
              return details.join(', ');
            });
          }).flat();
          
          throw new Error(`AI generated invalid boxes that exceed constraints:\n${violationDetails.join('\n')}\n\nPlease try again - the AI needs to create smaller boxes.`);
        }
        
        // Generate reasoning from Claude's response
        let displayReasoning = strategy; // Use Claude's packing strategy
        
        // Add summary information
        const summary = result.primary_recommendation?.summary || result.summary;
        if (summary) {
          displayReasoning += `\n\nSUMMARY: Created ${summary.total_boxes || totalBoxesCreated} boxes in ${optimizedBoxes.length} groups. `;
          displayReasoning += `${summary.zero_fee_groups || qualifyingGroups} groups qualify for zero placement fees. `;
          displayReasoning += `Average volume utilization: ${summary.avg_volume_utilization || 'N/A'}%. `;
          displayReasoning += `Estimated placement fees: ${summary.estimated_placement_fees || '$0.00'}.`;
        }
        
        // Add alternative options information
        if (result.alternative_options && result.alternative_options.length > 0) {
          displayReasoning += `\n\nALTERNATIVE OPTIONS: ${result.alternative_options.length} additional packing strategies available with different trade-offs between volume efficiency and handling complexity.`;
        }
        
        // Add leftover information
        if (result.leftover_units && result.leftover_units.length > 0) {
          const leftoverCount = result.leftover_units.reduce((sum, item) => sum + item.quantity, 0);
          displayReasoning += `\n\nLEFTOVERS: ${leftoverCount} units not allocated (${result.leftover_units.map(item => `${item.sku}: ${item.quantity}`).join(', ')}).`;
        }
        
        setOptimizationReasoning(displayReasoning);
        
        // Check if any groups were rejected
        const rejectedGroups = optimizedBoxes.filter(group => (group.box_count || 1) < USA_CONSTRAINTS.minIdenticalBoxes);
        const acceptedGroups = optimizedBoxes.filter(group => (group.box_count || 1) >= USA_CONSTRAINTS.minIdenticalBoxes);
        
        // Show success message with rejection info if needed
        let successMessage = `Optimization complete! Created ${totalBoxesCreated} boxes with ZERO placement fees.`;
        if (rejectedGroups.length > 0) {
          successMessage += `\n\nNote: ${rejectedGroups.length} box groups with fewer than 5 boxes were rejected and moved to leftovers to maintain zero-fee qualification.`;
        }
        alert(successMessage);
      } else {
        throw new Error('Invalid response format from Claude');
      }
      
    } catch (error) {
      console.error('Optimization failed:', error);
      alert('Optimization failed: ' + error.message);
    } finally {
      setOptimizing(false);
    }
  };

  const getTotalBoxes = () => {
    return Object.values(boxGroups).reduce((sum, groupData) => sum + (groupData.boxes ? groupData.boxes.length : 0), 0);
  };

  const getQualifyingBoxes = () => {
    return Object.values(boxGroups).filter(groupData => groupData.qualifiesForZeroFees).length;
  };

  const getPlacementFeeStatus = () => {
    const qualifying = getQualifyingBoxes();
    const totalGroups = Object.keys(boxGroups).length;
    return {
      qualifying,
      total: totalGroups,
      hasZeroFees: qualifying > 0 && totalGroups === qualifying
    };
  };

  const feeStatus = getPlacementFeeStatus();

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          height: '85vh',
          maxHeight: '85vh'
        }
      }}
    >
      <DialogTitle sx={{ py: 1.5 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center">
            <AssessmentIcon sx={{ mr: 1 }} />
            <Typography variant="h6">Plan Placement Fees</Typography>
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <DndProvider backend={HTML5Backend}>
          <Box sx={{ mb: 2 }}>
            <Alert
              severity={feeStatus.hasZeroFees ? 'success' : 'warning'}
              icon={feeStatus.hasZeroFees ? <CheckCircleIcon /> : <WarningIcon />}
            >
              <Box>
                <Typography variant="body2">
                  {feeStatus.hasZeroFees
                    ? 'All box groups qualify for zero placement fees!'
                    : `${feeStatus.qualifying} of ${feeStatus.total} box groups qualify for zero placement fees`}
                </Typography>
                <Typography variant="caption">
                  Need minimum {USA_CONSTRAINTS.minIdenticalBoxes} identical boxes per group
                </Typography>
              </Box>
            </Alert>
          </Box>

          {optimizationReasoning && (
            <Box sx={{ mb: 2 }}>
              <Alert severity="info" icon={<AutoAwesomeIcon />}>
                <Box>
                  <Typography variant="body2" fontWeight="bold" gutterBottom>
                    AI Optimization Reasoning:
                  </Typography>
                  <Typography variant="body2">
                    {optimizationReasoning}
                  </Typography>
                </Box>
              </Alert>
            </Box>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 1.5, height: 'fit-content' }}>
                <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold">Items</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {items.reduce((sum, item) => sum + item.quantity, 0)} / {items.reduce((sum, item) => sum + item.originalQuantity, 0)} remaining
                    </Typography>
                  </Box>
                  <Box display="flex" gap={1}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={autoOptimize}
                          onChange={(e) => setAutoOptimize(e.target.checked)}
                        />
                      }
                      label="Auto"
                    />
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={handleAutoOptimize}
                      disabled={optimizing}
                      startIcon={optimizing ? <RefreshIcon /> : <AutoAwesomeIcon />}
                      sx={{ fontSize: '0.75rem', minWidth: 'auto' }}
                    >
                      {optimizing ? 'Optimizing...' : 'Optimize'}
                    </Button>
                  </Box>
                </Box>
                {optimizing && <LinearProgress sx={{ mb: 2 }} />}
                <Box sx={{ maxHeight: 500, overflow: 'auto' }}>
                  {items.map(item => (
                    <ItemCard
                      key={item.id}
                      item={item}
                      onAdjustQuantity={handleAdjustQuantity}
                      allocatedQuantities={getAllocatedQuantities()}
                    />
                  ))}
                  {items.length === 0 && (
                    <Typography variant="body2" color="text.secondary" textAlign="center">
                      No items available
                    </Typography>
                  )}
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} md={8}>
              <Grid container spacing={1.5}>
                {Object.entries(boxGroups).map(([groupName, groupData]) => (
                  <Grid item xs={12} md={6} key={groupName}>
                    <BoxGroup
                      groupName={groupName}
                      groupData={groupData}
                      onDrop={handleDrop}
                      onRemoveBox={handleRemoveBox}
                      onDuplicateBox={handleDuplicateBox}
                      onAdjustBoxItemQuantity={handleAdjustBoxItemQuantity}
                    />
                  </Grid>
                ))}
                {Object.keys(boxGroups).length === 0 && (
                  <Grid item xs={12}>
                    <Paper sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
                      <Typography variant="body1" gutterBottom>
                        No box groups created yet
                      </Typography>
                      <Typography variant="body2">
                        Click "Optimize" to generate box groups or manually drag items to create boxes
                      </Typography>
                    </Paper>
                  </Grid>
                )}
              </Grid>
            </Grid>
          </Grid>
        </DndProvider>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!feeStatus.hasZeroFees}
          onClick={() => {
            // Save box configuration
            onClose();
          }}
        >
          Apply Configuration
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PlanPlacementFeesModal;